import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
  useTheme,
  Collapse,
} from "@mui/material";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../../services/UserService";

const initialValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const validationSchema = yup.object().shape({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .min(8, "Password must be at least 8 characters long")
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const PasswordResetForm = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // phases: null | "loading" | "success" | "error"
  const [phase, setPhase] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleFormSubmit = async (values, { setSubmitting, setErrors }) => {
    setErrorMessage("");
    setPhase("loading");
    setSubmitting(true);
    try {
      await changePassword(
        values.currentPassword,
        values.newPassword,
        values.confirmPassword,
      );
      setTimeout(() => {
        setPhase("success");
      }, 1000);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setTimeout(() => {
        setPhase("error");
        setErrorMessage(msg);
        setSubmitting(false);
        setTimeout(() => {
          setPhase(null);
          setErrorMessage("");
          setErrors({});
        }, 3000);
      }, 1000);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  const showAlert =
    phase === "success"
      ? { severity: "success", text: "Password changed successfully" }
      : phase === "error"
        ? { severity: "error", text: errorMessage }
        : formik.touched.currentPassword && formik.errors.currentPassword
          ? { severity: "error", text: formik.errors.currentPassword }
          : formik.touched.newPassword && formik.errors.newPassword
            ? { severity: "error", text: formik.errors.newPassword }
            : formik.touched.confirmPassword && formik.errors.confirmPassword
              ? { severity: "error", text: formik.errors.confirmPassword }
              : null;

  // Safe fallbacks to preserve styling during the Collapse exit animation
  const alertSeverity = showAlert?.severity || "error";
  const alertText = showAlert?.text || "";
  const isSuccess = alertSeverity === "success";

  return (
    <Box
      className="app"
      display="flex"
      width="100vw"
      height="100vh"
      sx={{ backgroundColor: colors.primary[500] }}
    >
      <Box className="content" display="flex" flexGrow={1}>
        <Container
          maxWidth="xs"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100%",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              minWidth: { xs: "100%", sm: "320px" },
              width: "100%",
              padding: { xs: "32px 20px", sm: "40px 32px" },
              borderRadius: "16px",
              backgroundColor:
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 12px 40px rgba(0, 0, 0, 0.4)"
                  : "0px 12px 40px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              animation: "fadeIn 0.6s ease-out forwards",
            }}
          >
            <Typography
              variant="h1"
              textAlign="center"
              fontWeight="800"
              color={colors.gray[100]}
              sx={{ letterSpacing: "-0.5px", mb: 1 }}
            >
              BFB<span style={{ color: colors.blueAccent[500] }}>.</span>
            </Typography>
            <Typography
              variant="subtitle1"
              textAlign="center"
              color={colors.gray[400]}
              fontWeight="500"
              mb={4}
            >
              Secure your account. Change your password.
            </Typography>

            <Collapse in={!!showAlert}>
              <Alert
                severity={alertSeverity}
                sx={{
                  backgroundColor: isSuccess
                    ? theme.palette.mode === "dark"
                      ? "rgba(76, 206, 172, 0.15)"
                      : "rgba(16, 185, 129, 0.1)"
                    : theme.palette.mode === "dark"
                      ? "rgba(244, 63, 94, 0.15)"
                      : "rgba(239, 68, 68, 0.1)",
                  color: isSuccess
                    ? colors.greenAccent[500]
                    : colors.redAccent[500],
                  border: `1px solid ${isSuccess ? colors.greenAccent[500] : colors.redAccent[500]}`,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 3,
                  borderRadius: "8px",
                  "& .MuiAlert-icon": {
                    color: isSuccess
                      ? colors.greenAccent[500]
                      : colors.redAccent[500],
                  },
                }}
              >
                {alertText}
              </Alert>
            </Collapse>

            <form onSubmit={formik.handleSubmit}>
              <TextField
                label="Current Password"
                variant="filled"
                fullWidth
                margin="normal"
                name="currentPassword"
                type={showCurrent ? "text" : "password"}
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.currentPassword &&
                  Boolean(formik.errors.currentPassword)
                }
                helperText={
                  formik.touched.currentPassword &&
                  formik.errors.currentPassword
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.04)",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    "& input:-webkit-autofill": {
                      WebkitBorderRadius: "8px",
                      borderRadius: "8px",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.02)",
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showCurrent ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowCurrent(!showCurrent)}
                        edge="end"
                        sx={{
                          color: colors.gray[400],
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:active": { transform: "scale(0.8)" },
                          "&:hover": { color: colors.gray[300] },
                        }}
                      >
                        {showCurrent ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.95rem", color: colors.gray[400] },
                }}
              />

              <TextField
                label="New Password"
                variant="filled"
                fullWidth
                margin="normal"
                name="newPassword"
                type={showNew ? "text" : "password"}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.04)",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    "& input:-webkit-autofill": {
                      WebkitBorderRadius: "8px",
                      borderRadius: "8px",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.02)",
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showNew ? "Hide password" : "Show password"}
                        onClick={() => setShowNew(!showNew)}
                        edge="end"
                        sx={{
                          color: colors.gray[400],
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:active": { transform: "scale(0.8)" },
                          "&:hover": { color: colors.gray[300] },
                        }}
                      >
                        {showNew ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.95rem", color: colors.gray[400] },
                }}
              />

              <TextField
                label="Confirm New Password"
                variant="filled"
                fullWidth
                margin="normal"
                name="confirmPassword"
                type={showConfirm ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.04)",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    "& input:-webkit-autofill": {
                      WebkitBorderRadius: "8px",
                      borderRadius: "8px",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.02)",
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showConfirm ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowConfirm(!showConfirm)}
                        edge="end"
                        sx={{
                          color: colors.gray[400],
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:active": { transform: "scale(0.8)" },
                          "&:hover": { color: colors.gray[300] },
                        }}
                      >
                        {showConfirm ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.95rem", color: colors.gray[400] },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={
                  !formik.values.currentPassword ||
                  !formik.values.newPassword ||
                  !formik.values.confirmPassword ||
                  formik.isSubmitting
                }
                sx={{
                  marginTop: "32px",
                  padding: "12px 0",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: colors.blueAccent[600],
                  color: "#ffffff",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 14px rgba(99, 102, 241, 0.4)"
                      : "0 4px 14px rgba(79, 70, 229, 0.3)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[500],
                    transform: "translateY(-2px)",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 6px 20px rgba(99, 102, 241, 0.5)"
                        : "0 6px 20px rgba(79, 70, 229, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.12)",
                    color:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    boxShadow: "none",
                  },
                }}
              >
                {phase === "loading" ? (
                  <>
                    Changing Password&nbsp;
                    <CircularProgress
                      size={18}
                      sx={{ color: "inherit", ml: "12px" }}
                    />
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </form>
          </Box>
        </Container>
      </Box>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default PasswordResetForm;
